import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { ArrowLeft, MapPin, Users, Target, Heart, Shield, Zap } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const AboutPage = () => {
  const { t, language } = useLanguage();

  const values = [
    {
      icon: Shield,
      title: language === 'es' ? 'Cumplimiento' : 'Compliance',
      description: language === 'es' 
        ? 'Nos aseguramos de que tu negocio siempre esté al día con la normativa fiscal española.'
        : 'We ensure your business is always up to date with Spanish tax regulations.'
    },
    {
      icon: Zap,
      title: language === 'es' ? 'Simplicidad' : 'Simplicity',
      description: language === 'es'
        ? 'Tecnología compleja hecha sencilla. No necesitas ser experto para usar VendFactura.'
        : 'Complex technology made simple. You don\'t need to be an expert to use VendFactura.'
    },
    {
      icon: Heart,
      title: language === 'es' ? 'Cercanía' : 'Proximity',
      description: language === 'es'
        ? 'Soporte humano y cercano. Entendemos los retos de los pequeños comercios españoles.'
        : 'Human and close support. We understand the challenges of small Spanish businesses.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      <main>
        {/* Hero */}
        <section className="vf-section bg-[#1e3a5f] text-white">
          <div className="vf-container">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'es' ? 'Volver' : 'Back'}
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <h1 className="vf-heading-1 mb-6">
                {language === 'es' ? 'Sobre Nosotros' : 'About Us'}
              </h1>
              <p className="vf-body-large text-white/80">
                {language === 'es' 
                  ? 'VendFactura nace de la necesidad de ayudar a los pequeños comercios españoles a cumplir con la normativa VeriFactu sin complicaciones.'
                  : 'VendFactura was born from the need to help small Spanish businesses comply with VeriFactu regulations without complications.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Made in Spain */}
        <section className="vf-section">
          <div className="vf-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-full">
                  <MapPin className="w-4 h-4" />
                  <span className="font-semibold">Hecho en España</span>
                </div>
                
                <h2 className="vf-heading-2 text-[#1e3a5f]">
                  {language === 'es' 
                    ? 'Conocemos tu Negocio'
                    : 'We Know Your Business'}
                </h2>
                
                <p className="vf-body-large text-slate-600">
                  {language === 'es'
                    ? 'Somos un equipo español que entiende las necesidades específicas de las tiendas de barrio, los estancos, los quioscos y los autónomos. Sabemos lo que significa lidiar con la burocracia de Hacienda.'
                    : 'We are a Spanish team that understands the specific needs of neighborhood stores, tobacco shops, newsstands and freelancers. We know what it means to deal with Tax Agency bureaucracy.'}
                </p>
                
                <p className="vf-body text-slate-600">
                  {language === 'es'
                    ? 'VendFactura es nuestra respuesta a la pregunta: "¿Cómo puedo cumplir con VeriFactu sin perder tiempo ni dinero?"'
                    : 'VendFactura is our answer to the question: "How can I comply with VeriFactu without wasting time or money?"'}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1723926579572-1dbcd1369817?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxncm9jZXJ5JTIwc3RvcmUlMjBzaGVsdmVzJTIwc3BhaW4lMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzMyMjIwMjB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Spanish grocery store"
                  className="w-full h-auto border-2 border-[#1e3a5f]"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#22c55e] text-white p-4 font-bold">
                  🇪🇸 100% Español
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="vf-section bg-[#F8FAFC]">
          <div className="vf-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="vf-heading-2 text-[#1e3a5f] mb-4">
                {language === 'es' ? 'Nuestros Valores' : 'Our Values'}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="vf-card text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#1e3a5f] rounded-sm flex items-center justify-center">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e3a5f] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="vf-section">
          <div className="vf-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-slate-100 rounded-full">
                <Users className="w-5 h-5 text-[#1e3a5f]" />
                <span className="font-medium text-[#1e3a5f]">
                  {language === 'es' ? 'Nuestro Equipo' : 'Our Team'}
                </span>
              </div>
              
              <h2 className="vf-heading-2 text-[#1e3a5f] mb-6">
                {language === 'es' 
                  ? 'Pequeño Equipo, Gran Impacto'
                  : 'Small Team, Big Impact'}
              </h2>
              
              <p className="vf-body-large text-slate-600">
                {language === 'es'
                  ? 'Somos un equipo reducido de desarrolladores, diseñadores y expertos en fiscalidad española. Cada uno de nosotros está comprometido con hacer que tu negocio cumpla la normativa sin estrés.'
                  : 'We are a small team of developers, designers and Spanish tax experts. Each of us is committed to making your business compliant without stress.'}
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
